// "Enviamos a proposta" (05/10/2026). Pedido do Lucas: "se a gente enviou
// proposta para a oportunidade, não é necessário aparecer o alerta de proposta
// vencida … falta marcar um botão que a gente encaminhou proposta".
//
// Grava quando foi (hora de parede de Brasília, como todo prazo de
// licitação), o protocolo e, se quiser, o preço proposto. O servidor
// (`lic_registrar_envio`) leva a licitação para a Sessão quando ela está antes
// disso e liga o acompanhamento pelo PNCP. O modal não faz POST: o pai chama.
import { useState } from 'react';
import { Modal, Input, InputNumber, Typography, Form, Alert } from 'antd';
import { PROPS_DINHEIRO_BR } from '../../../utils/dinheiroInput';

const { Text } = Typography;

export interface DadosEnvio {
  enviada_em: string;
  protocolo?: string;
  preco_proposto?: number | null;
}

const doisDigitos = (n: number) => String(n).padStart(2, '0');
/** Agora, como o `datetime-local` mostra: 'AAAA-MM-DDTHH:mm' na hora do computador. */
export function agoraParaCampo(d: Date = new Date()): string {
  return `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}T${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
}

const fmtParede = (s?: string | null) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} às ${m[4]}:${m[5]}` : null;
};

export function ModalEnvioProposta({ titulo, prazoProposta, precoAtual, vaiParaSessao, onCancelar, onConfirmar }: {
  titulo: string;
  /** Prazo da proposta (parede BRT) — só para avisar se a hora informada passou dele. */
  prazoProposta?: string | null;
  precoAtual?: number | null;
  /** A licitação está antes da Sessão: o servidor vai movê-la. */
  vaiParaSessao: boolean;
  onCancelar: () => void;
  onConfirmar: (dados: DadosEnvio) => Promise<void> | void;
}) {
  const [quando, setQuando] = useState(agoraParaCampo());
  const [protocolo, setProtocolo] = useState('');
  const [preco, setPreco] = useState<number | null>(precoAtual != null ? Number(precoAtual) : null);
  const [gravando, setGravando] = useState(false);

  const valido = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(quando);
  const noFuturo = valido && quando > agoraParaCampo(new Date(Date.now() + 10 * 60000));
  const depoisDoPrazo = valido && !!prazoProposta && quando > String(prazoProposta).slice(0, 16);

  const confirmar = async () => {
    setGravando(true);
    try {
      await onConfirmar({ enviada_em: quando, protocolo: protocolo.trim() || undefined, preco_proposto: preco });
    } finally { setGravando(false); }
  };

  return (
    <Modal open title="Enviamos a proposta" onCancel={onCancelar} onOk={confirmar} destroyOnHidden
      okText="Registrar o envio" cancelText="Voltar" confirmLoading={gravando}
      okButtonProps={{ disabled: !valido || noFuturo }}
      width="94vw" style={{ maxWidth: 560, top: 24 }}>
      <Text strong style={{ display: 'block', marginBottom: 10 }}>{titulo}</Text>
      <Form layout="vertical" size="small">
        <Form.Item label="Quando foi enviada" required
          validateStatus={noFuturo ? 'error' : depoisDoPrazo ? 'warning' : undefined}
          help={noFuturo ? 'A hora está no futuro.'
            : depoisDoPrazo ? `O prazo da proposta era ${fmtParede(prazoProposta)} — confira a hora.` : undefined}>
          <Input type="datetime-local" value={quando} onChange={e => setQuando(e.target.value)} style={{ width: 240 }} />
        </Form.Item>
        <Form.Item label="Protocolo ou observação" extra="Ex.: protocolo 2026/1234 no Licitanet; enviada pelo Gustavo.">
          <Input.TextArea value={protocolo} onChange={e => setProtocolo(e.target.value)} rows={2} maxLength={500} showCount />
        </Form.Item>
        <Form.Item label="Preço proposto (R$)" extra="Opcional. Fica na licitação e serve depois para comparar com o vencedor.">
          <InputNumber {...(PROPS_DINHEIRO_BR as any)} value={preco} onChange={(v) => setPreco(v == null ? null : Number(v))}
            min={0} style={{ width: 220 }} />
        </Form.Item>
      </Form>
      <Alert type="info" showIcon
        message={vaiParaSessao ? 'A licitação passa para a etapa Sessão.' : 'A licitação continua na etapa em que está.'}
        description={'O prazo da proposta deixa de aparecer como alerta. O sistema segue acompanhando a licitação no PNCP todo dia '
          + '(situação, documentos novos, resultado) e avisa do que mudar o responsável e a lista de cópia das Configurações.'} />
    </Modal>
  );
}
